// apps/client/src/engine/perf.ts — developer perf overlay (spec §8.7). Shown only when asked for: settings.perfOverlay (the Settings
// toggle), or open the game with `?perf` to turn it on (remembered in the settings) and `?perf=0` to turn it off again. Developer-only,
// so its labels are not player-facing strings.
import type { RenderStats } from './renderer';   // type-only: renderer.ts imports the functions below at runtime, so no cycle

/** The `?perf` URL flag: true for `?perf` / `?perf=1`, false for `?perf=0`, null when absent. */
export function perfFlag(search: string): boolean | null {
  const v = new URLSearchParams(search).get('perf');
  return v === null ? null : v !== '0' && v !== 'false';
}

interface MemoryInfo { usedJSHeapSize: number }

/** What the overlay must never paint over: anything you can tap or type into, the HUD, panels and the dialogue bubble. */
const UI = 'button, a[href], input, select, textarea, [role="button"], [tabindex]:not([tabindex="-1"]), .hit, [data-hud] > *, .panel-frame, .dialogue';
/** Where it may sit, in order: the bottom centre (between the Chat/Emotes column and the action pad), else the top centre (between the
 *  clock and the bells). */
const SLOTS = [{ bottom: '4px', top: '' }, { bottom: '', top: '4px' }] as const;
interface Box { left: number; top: number; right: number; bottom: number }
const overlaps = (a: Box, b: Box) => Math.min(a.right, b.right) - Math.max(a.left, b.left) > 1 && Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top) > 1;

export class PerfOverlay {
  private readonly el: HTMLPreElement;
  constructor(parent: HTMLElement) {
    this.el = document.createElement('pre');
    this.el.dataset.perf = '';
    this.el.style.cssText = 'pointer-events:none;position:fixed;left:50%;transform:translateX(-50%);bottom:4px;margin:0;padding:4px 8px;'
      + 'border-radius:8px;z-index:50;'
      + 'font:11px/1.35 ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;color:rgba(255,251,241,.85);'
      + 'background:rgba(36,50,40,.55);white-space:pre;';
    parent.append(this.el);
    // Re-placed the moment UI mounts or unmounts, not at the next refresh (M2 playtest: at 2 fps it sat over a new panel's buttons).
    this.watch = typeof MutationObserver === 'undefined' ? null : new MutationObserver(() => this.place());
    this.watch?.observe(document.body, { childList: true, subtree: true });
  }
  private readonly watch: MutationObserver | null;
  /** Puts the overlay in the first slot where it covers no UI (M2 playtest: at the bottom centre it covered the villager talk's
   *  choice row), and hides it when there is none, or while any panel is open (a centred panel reaches both slots). */
  private place(): void {
    const s = this.el.style;
    const ui = document.querySelector('.panel-frame') ? null : [...document.querySelectorAll<HTMLElement>(UI)].filter((e) => !this.el.contains(e))
      .map((e) => e.getBoundingClientRect()).filter((r) => r.width >= 1 && r.height >= 1);
    // Measured laid out: a display:none element's rect is all zeros, so it would fit anywhere and every hide would be undone (v1 replay).
    // Shown and re-hidden within this task, so it never paints in between.
    if (ui && s.display === 'none') s.display = '';
    const slot = ui && SLOTS.find((p) => {
      s.bottom = p.bottom; s.top = p.top;
      return !ui.some((r) => overlaps(this.el.getBoundingClientRect(), r));
    });
    if (!slot) { if (s.display !== 'none') s.display = 'none'; return; }
    s.bottom = slot.bottom; s.top = slot.top;
  }
  /** Refreshes the numbers, then re-places it (the text's width changes with them). */
  update(s: RenderStats): void {
    const mem = (performance as Performance & { memory?: MemoryInfo }).memory;
    const lines = [
      `${s.fps.toFixed(0)} fps  ${s.frameMs.toFixed(1)} ms  calls ${s.calls}  scene ${s.sceneCalls}  tris ${s.triangles}`,
      `geo ${s.geometries}  tex ${s.textures}  scale ${s.scale.toFixed(2)}  q ${s.quality}${mem ? `  mem ${(mem.usedJSHeapSize / 1048576).toFixed(0)} MB` : ''}`,
    ];
    this.el.textContent = lines.join('\n');
    this.place();
  }
  dispose(): void { this.watch?.disconnect(); this.el.remove(); }
}

// ---------- M2-T12 (rev 5 items 9 and 21): one sample per second from renderer.ts render(), overlay shown or not; e2e reads it via __wt.perf()
/** One second of rendering. `drawCalls` is the whole frame; the §8.7 budget (tuning.render.maxDrawCalls) applies to `sceneCalls`. */
export interface PerfSample { at: number; fps: number; frameMs: number; drawCalls: number; sceneCalls: number; triangles: number; memoryMB: number | null;
  pixelRatio: number; scale: number; quality: 'low' | 'high' }
export function toPerfSample(s: RenderStats, at: number): PerfSample {
  const mem = (performance as Performance & { memory?: MemoryInfo }).memory;
  return { at, fps: s.fps, frameMs: s.frameMs, drawCalls: s.calls, sceneCalls: s.sceneCalls, triangles: s.triangles,
    memoryMB: mem ? Math.round(mem.usedJSHeapSize / 1_048_576) : null, pixelRatio: s.pixelRatio, scale: s.scale, quality: s.quality };
}
let last: PerfSample | null = null;
export function recordPerfSample(s: PerfSample): void { last = s; }
export function lastPerfSample(): PerfSample | null { return last; }
